import _isEqual from 'lodash/isEqual';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { motion } from 'framer-motion';
import { SavedFilter } from '../../types';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { useSelectedFilter } from '../../SelectedFilterContext';
import { useSelectedFilterOptions } from '../../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import * as React from 'react';
import clsx from 'clsx';

const messages = defineMessages({
  noFilterSelected: {
    id: 'omicsExplorer.savedFilters.noFilterSelected',
    defaultMessage: 'Saved filters',
  },
  loading: {
    id: 'omicsExplorer.savedFilters.loading',
    defaultMessage: 'Fetching saved filters…',
  },
  modifiedChip: {
    id: 'omicsExplorer.savedFilters.modifiedChip',
    defaultMessage: 'Modified',
  },
  default: {
    id: 'omicsExplorer.savedFilters.default',
    defaultMessage: 'Current Default',
  },
  selectASavedFilter: {
    id: 'omicsExplorer.savedFilters.selectASavedFilter',
    defaultMessage: 'Select a saved filter',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  fullWidth: {
    width: '100%',
  },
  modifiedChip: {
    backgroundColor: theme.palette.green.main,
    borderRadius: theme.pxToRem(4),
    color: theme.palette.common.white,
    fontSize: theme.typography.caption.fontSize,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    pointerEvents: 'none',
    position: 'absolute',
    right: 60,
    top: 14,
  },
}));

interface SavedFiltersDropdownProps {
  className?: string;
  currentDefaultFilterId?: string;
  isLoading?: boolean;
  onFilterMenuItemClick?: (filter: SavedFilter) => any;
  options?: Array<SavedFilter>;
}

export const SavedFiltersDropdown: React.FC<SavedFiltersDropdownProps> = ({
  className,
  isLoading,
  onFilterMenuItemClick,
  options = [],
  currentDefaultFilterId,
  ...rootProps
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [selectedFilter] = useSelectedFilter();
  const [selectedFilterOptions] = useSelectedFilterOptions();

  const [areFiltersModified, setAreFiltersModified] = React.useState(false);

  // We will use a ref to determine when our selectedFilter has changed
  const selectedFilterRef = React.useRef(null);

  React.useEffect(() => {
    if (!selectedFilter) {
      setAreFiltersModified(false);
      selectedFilterRef.current = null;
      return;
    }

    // Using the ref from above, we determine when the selected filter has
    // changed so that we can reset the modified chip
    if (!_isEqual(selectedFilterRef.current, selectedFilter)) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      selectedFilterRef.current = selectedFilter;
      setAreFiltersModified(false);
    }

    if (!selectedFilter?.params?.filters) {
      return;
    }

    if (!_isEqual(selectedFilterOptions, selectedFilter.params.filters)) {
      setAreFiltersModified(true);
    } else {
      setAreFiltersModified(false);
    }
  }, [selectedFilter, selectedFilterOptions]);

  const handleSavedFilterClick = (_clickedId: string, meta: SavedFilter) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    onFilterMenuItemClick(meta);
  };

  return (
    <>
      <Select
        className={clsx(classes.fullWidth, className)}
        disabled={isLoading || options.length === 0}
        label=""
        aria-label={formatMessage(messages.selectASavedFilter)}
        popoverAriaLabel={formatMessage(messages.selectASavedFilter)}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        value={selectedFilter?.id || null}
        onChange={(id: string, meta: SavedFilter) =>
          handleSavedFilterClick(id, meta)
        }
        fullWidth
        {...rootProps}
      >
        {options?.map(filterOption => {
          return (
            <SelectOption
              key={filterOption?.id}
              value={filterOption?.id}
              title={filterOption?.name}
              meta={filterOption}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              subtitle={
                currentDefaultFilterId === filterOption?.id
                  ? formatMessage(messages.default)
                  : null
              }
            />
          );
        })}
        <SelectOption
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          value={null}
          title={
            isLoading
              ? formatMessage(messages.loading)
              : formatMessage(messages.noFilterSelected)
          }
        />
      </Select>
      {areFiltersModified && !isLoading && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          className={classes.modifiedChip}
        >
          {formatMessage(messages.modifiedChip)}
        </motion.span>
      )}
    </>
  );
};
